import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { FormContextData } from "@thalamos/aspire-common";

import { routeFns } from "../../../routePaths.js";
import { startLinkedMcaForm } from "./startLinkedMcaForm.js";
import {
  Banner,
  BannerList,
  renderErrorToast,
} from "~/components/design-system/index.js";

// Injectable so the component can be exercised without a server.
export type StartLinkedMcaForm = typeof startLinkedMcaForm;

/**
 * Invites the clinician to complete the MCA form that partners the one they have
 * just finished. Accepting adds that form to this same assessment.
 */
export function McaContextualPrompt({
  formContext,
  templateId,
  startForm = startLinkedMcaForm,
}: {
  formContext: FormContextData;
  templateId: string;
  startForm?: StartLinkedMcaForm;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);

  const copy = `pages.mcaAssessment.prompt.${templateId}`;

  return (
    <Box sx={{ mb: 3 }} data-testid={`mca-prompt:${templateId}`}>
      <Banner
        title={t(`${copy}.heading`)}
        body={[t(`${copy}.body`)]}
        bannerType={BannerList.INFO}
        rightButtonLabel={t(`${copy}.button`)}
        rightButtonTestId={`mca-prompt-start:${templateId}`}
        onRightButton={async () => {
          if (starting) return;
          setStarting(true);
          try {
            const started = await startForm({
              formContextId: formContext.id,
              patientId: formContext.patientId,
              formTemplateId: templateId,
            });

            if (!started.ok) {
              renderErrorToast({
                message:
                  started.refusal ?? t("pages.mcaAssessment.failedCreateForm"),
              });
              return;
            }

            navigate(
              routeFns.formDraftsComplete(
                started.formDraftId,
                formContext.patientId,
              ),
            );
          } finally {
            setStarting(false);
          }
        }}
      />
    </Box>
  );
}
